import { processApiError } from '@repo/utils';
import { CountriesFilterLocal } from '@/pages/agents/Countries/countries-query-reducer';
import {
  AdminCountriesResponseDto,
  GetAdminCountriesDto,
  CreateCountryDto,
  UpdateCountryDto,
  CreateCountrySubdivisionDto,
  UpdateCountrySubdivisionDto,
  Country,
  CountrySubdivision,
  AdminCountriesSubdivisionsFilterDto,
  GetAdminCountriesSubdivisionsDto,
  AdminCountriesSubdivisionsResponseDto,
} from '@repo/common-types';

import { api } from '../api';

/**
 * Convert local filter format to API format for countries
 */
export const convertCountriesFilterToApiFormat = (
  localFilter: CountriesFilterLocal,
): GetAdminCountriesDto['filter'] => {
  const apiFilter: GetAdminCountriesDto['filter'] = {};

  if (localFilter.search) apiFilter.search = localFilter.search;
  if (localFilter.code) apiFilter.code = localFilter.code;
  if (localFilter.regionName) apiFilter.regionName = localFilter.regionName;
  if (localFilter.subdivisionName) apiFilter.subdivisionName = localFilter.subdivisionName;

  // Convert boolean to string for API
  if (typeof localFilter.hasSubdivisions === 'boolean') {
    apiFilter.hasSubdivisions = localFilter.hasSubdivisions ? 'true' : 'false';
  }

  return apiFilter;
};

/**
 * Fetch countries list with filters, pagination, and sorting
 */
export const fetchCountriesRest = async (
  params: {
    skip: number;
    take: number;
    filter?: CountriesFilterLocal;
    sorts?: Array<{ field: string; order: any }>;
  },
  config?: {
    signal?: AbortSignal;
  },
): Promise<AdminCountriesResponseDto> => {
  try {
    // Convert local filter to API format
    const apiParams: GetAdminCountriesDto = {
      skip: params.skip,
      take: params.take,
      sorts: params.sorts,
      filter: params.filter ? convertCountriesFilterToApiFormat(params.filter) : undefined,
    };

    const response = await api.get('/v1/admin/regions/countries', { params: apiParams, signal: config?.signal });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Create a new country
 */
export const createCountryRest = async (countryData: CreateCountryDto): Promise<Country> => {
  try {
    const response = await api.post('/v1/admin/regions/countries', countryData);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Update an existing country
 */
export const updateCountryRest = async (countryId: string, countryData: UpdateCountryDto): Promise<Country> => {
  try {
    const response = await api.put(`/v1/admin/regions/countries/${countryId}`, countryData);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Delete a country
 */
export const deleteCountryRest = async (countryId: string): Promise<void> => {
  try {
    await api.delete(`/v1/admin/regions/countries/${countryId}`);
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Get a single country with full details
 */
export const getCountryRest = async (countryId: string): Promise<Country> => {
  try {
    const response = await api.get(`/v1/admin/regions/countries/${countryId}`);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

export const fetchCountrySubdivisionsRest = async (
  params: {
    skip: number;
    take: number;
    filter?: AdminCountriesSubdivisionsFilterDto;
    sorts?: Array<{ field: string; order: any }>;
  },
  config?: {
    signal?: AbortSignal;
  },
): Promise<AdminCountriesSubdivisionsResponseDto> => {
  try {
    const apiParams: GetAdminCountriesSubdivisionsDto = {
      skip: params.skip,
      take: params.take,
      filter: params.filter,
      sorts: params.sorts,
    };
    const response = await api.get('/v1/admin/regions/subdivisions', { params: apiParams, signal: config?.signal });
    const data = response.data as AdminCountriesSubdivisionsResponseDto;
    return data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Create a new subdivision for a country
 */
export const createSubdivisionRest = async (
  subdivisionData: CreateCountrySubdivisionDto,
): Promise<CountrySubdivision> => {
  try {
    const response = await api.post('/v1/admin/regions/subdivisions', subdivisionData);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Update an existing subdivision
 */
export const updateSubdivisionRest = async (
  subdivisionId: string,
  subdivisionData: UpdateCountrySubdivisionDto,
): Promise<CountrySubdivision> => {
  try {
    const response = await api.put(`/v1/admin/regions/subdivisions/${subdivisionId}`, subdivisionData);
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Delete a subdivision
 */
export const deleteSubdivisionRest = async (subdivisionId: string): Promise<void> => {
  try {
    await api.delete(`/v1/admin/regions/subdivisions/${subdivisionId}`);
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Bulk import subdivisions for a country (placeholder for future implementation)
 */
export const bulkImportSubdivisionsRest = async (
  countryId: string,
  subdivisions: Array<{ name: string; code: string }>,
): Promise<CountrySubdivision[]> => {
  try {
    const response = await api.post(`/v1/admin/regions/countries/${countryId}/subdivisions/bulk-import`, {
      subdivisions,
    });
    return response.data;
  } catch (error) {
    const userFriendlyMessage = processApiError(error);
    throw new Error(userFriendlyMessage);
  }
};
